import {
  ONBOARDINGPOPUP_CLOSED_EVENT,
  ONBOARDINGPOPUP_SHOWN_EVENT,
} from "./events";

const template = document.createElement("template");

template.innerHTML = /* HTML */ `
  <style>
    :host {
      --transition-time: 500ms;

      display: block;
      position: absolute;
      transition:
        opacity var(--transition-time),
        display var(--transition-time) allow-discrete,
        translate var(--transition-time);
      z-index: 1;

      @starting-style {
        opacity: 0;
        translate: 0 1rem;
      }
    }

    :host([hidden]) {
      display: none;
      opacity: 0;
      translate: 0 1rem;
    }

    /* using inset with anchor() instead of "position-area" because position-area does not work for our specific article markup. */
    :host([position~="top"]) {
      inset-block-end: anchor(start);
      margin-block-end: 1.25rem;
    }

    :host([position~="left"]) {
      inset-inline-start: anchor(start);
      margin-inline-end: var(--article-padding-inline);
    }

    :host([position~="right"]) {
      inset-inline-end: anchor(end);
      margin-inline-start: var(--article-padding-inline);
    }

    .onboarding-popup {
      --padding: 1rem;

      background-color: var(--color-bg-secondary);
      border: 1px solid var(--color-bd-subtle-300);
      border-radius: 0.25rem;
      box-sizing: border-box;
      display: inline-flex;
      max-inline-size: 15.4375rem;
      padding: var(--padding);
      position: relative;

      @media (prefers-color-scheme: light) {
        filter: drop-shadow(0 4px 16px rgba(25, 25, 25, 0.08))
          drop-shadow(0 2px 4px rgba(25, 25, 25, 0.04));
        will-change: filter; /* Promote to GPU layer to aviod flickering in safari */
      }
    }

    .onboarding-popup::after {
      --size: 1rem;

      background-color: var(--color-bg-secondary);
      block-size: var(--size);
      border-block-end: inherit;
      border-inline-end: inherit;
      border-end-end-radius: 0.125rem;
      box-sizing: border-box;
      content: "";
      inline-size: var(--size);
      inset-block-end: calc(var(--size) / -2 - 1px);
      position: absolute;
      rotate: 45deg;
    }

    :host([position~="left"]) .onboarding-popup::after {
      inset-inline-start: var(--padding);
    }

    :host([position~="right"]) .onboarding-popup::after {
      inset-inline-end: var(--padding);
    }

    slot[name="title"] {
      display: block;
      margin-block-end: 0.5rem;
      margin-inline-end: 1rem; /* offset to avoid collision with absolute positioned close button */
    }

    slot[name="content"] {
      color: var(--color-txt-subtle-600);
    }

    button {
      background: var(--color-bg-secondary);
      border: none;
      border-radius: 0.25rem;
      color: var(--color-txt-default);
      cursor: pointer;
      display: inline-flex;
      inset-block-start: 0.25rem;
      inset-inline-end: 0.25rem;
      padding: 0.25rem;
      position: absolute;
    }

    button::before {
      content: var(--ws-onboarding-popup-close-icon-content);
      font: normal 1rem/1 var(--ws-onboarding-popup-close-icon-font-family);
    }
  </style>

  <div class="onboarding-popup" role="tooltip">
    <div>
      <slot name="title"></slot>
      <slot name="content"></slot>
    </div>
    <button aria-label="Schließen"></button>
  </div>
`;

export class OnboardingPopup extends HTMLElement {
  #target: HTMLElement | null | undefined;
  #button: HTMLButtonElement;
  #observer: IntersectionObserver | undefined;
  #isClosed = false;
  #anchorName = `--onboarding-popup-${crypto.randomUUID()}`;

  get #delay() {
    const delay = this.getAttribute("delay");
    if (!delay) return;

    return parseInt(delay, 10);
  }

  constructor() {
    super();

    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    this.#button = this.shadowRoot!.querySelector("button")!;
  }

  connectedCallback() {
    const isOnboarding = window.location.hash === "#onboarding";
    if (!isOnboarding) return;

    const selector = this.getAttribute("target");
    if (!selector) return;

    this.#target = document.querySelector<HTMLElement>(selector);
    if (!this.#target) return;

    this.#observer = new IntersectionObserver(
      this.#observerCallback.bind(this),
      { threshold: 0.5 },
    );
    this.#observer.observe(this.#target);
    this.#button.addEventListener("click", () => this.#close());

    this.#target.style.anchorName = this.#anchorName;
    this.style.positionAnchor = this.#anchorName;

    this.#loadAnchorPositioningPolyfill();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#button.removeEventListener("click", () => this.#close());
  }

  #observerCallback([entry]: IntersectionObserverEntry[]) {
    if (!entry?.isIntersecting) return;

    if (this.#delay) {
      setTimeout(() => this.#show(), this.#delay);
    } else {
      this.#show();
    }
  }

  #show() {
    if (this.#isClosed) return;

    this.removeAttribute("hidden");
    this.dispatchEvent(new CustomEvent(ONBOARDINGPOPUP_SHOWN_EVENT));
  }

  #close() {
    this.#isClosed = true;
    this.setAttribute("hidden", "");
    this.dispatchEvent(new CustomEvent(ONBOARDINGPOPUP_CLOSED_EVENT));
  }

  // Setup styles attributes outside of shadowdom for anchor positioning polyfill
  async #loadAnchorPositioningPolyfill() {
    if (CSS.supports("anchor-name: --a")) return;
    if (!this.#target) return;

    const targetInlineStyles = this.#target.getAttribute("style") ?? "";
    const thisInlineStyles = this.getAttribute("style") ?? "";
    const position = this.getAttribute("position") ?? "";
    const anchorInset = [];

    if (position.includes("top")) {
      anchorInset.push("inset-block-end: anchor(start);");
    }
    if (position.includes("left")) {
      anchorInset.push("inset-inline-start: anchor(start);");
    }
    if (position.includes("right")) {
      anchorInset.push("inset-inline-end: anchor(end);");
    }

    this.#target.setAttribute(
      "style",
      `anchor-name: ${this.#anchorName}; ${targetInlineStyles}`,
    );
    this.setAttribute(
      "style",
      `position-anchor: ${this.#anchorName}; ${anchorInset.join(" ")}; ${thisInlineStyles}`,
    );

    const { default: polyfill } =
      await import("@oddbird/css-anchor-positioning/fn");
    polyfill();
  }
}

"customElements" in window &&
  customElements.get("ws-onboarding-popup") === undefined &&
  customElements.define("ws-onboarding-popup", OnboardingPopup);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-onboarding-popup": OnboardingPopup;
  }
}
